import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const piliers = [
  { icon: 'ri-shield-star-line', titleKey: 'pourquoi_1_title', descKey: 'pourquoi_1_desc' },
  { icon: 'ri-file-shield-line', titleKey: 'pourquoi_2_title', descKey: 'pourquoi_2_desc' },
  { icon: 'ri-time-line', titleKey: 'pourquoi_3_title', descKey: 'pourquoi_3_desc' },
  { icon: 'ri-snowflake-line', titleKey: 'pourquoi_4_title', descKey: 'pourquoi_4_desc' },
];

export default function PourquoiNous() {
  const { t } = useTranslation('toiture');
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>(0.1);

  return (
    <section id="pourquoi" className="bg-white py-20 md:py-28 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        <div
          ref={titleRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl">
            {t('pourquoi_h2')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: Image */}
          <div
            className={`lg:col-span-4 transition-all duration-1000 delay-100 ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="rounded-lg overflow-hidden sticky top-24">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20metal%20standing%20seam%20roof%20on%20a%20contemporary%20Quebec%20house%20with%20snow%20guards%2C%20dark%20grey%20steel%20panels%2C%20sharp%20clean%20lines%2C%20winter%20scene%20with%20light%20snow%20on%20edges%2C%20warm%20late%20afternoon%20light%20hitting%20the%20roof%2C%20architectural%20photography%20style%2C%20no%20text%20visible&width=500&height=700&seq=toi-pq1&orientation=portrait"
                alt="Toiture tôle métal moderne installée à Lévis par couvreur certifié RBQ"
                className="w-full h-80 lg:h-[520px] object-cover object-top"
                loading="lazy"
                width="500"
                height="700"
              />
              <div className="bg-slate-primary p-4">
                <p className="text-white/90 text-sm font-medium">
                  <i className="ri-shield-star-line mr-2 text-copper" />
                  {t('pourquoi_1_title')}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Grid */}
          <div
            ref={gridRef}
            className={`lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 transition-all duration-1000 delay-200 ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {piliers.map((pilier, index) => (
              <div
                key={index}
                className="bg-soft-gray rounded-lg p-6 md:p-7 text-center hover:shadow-md transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-copper/10 mb-5 mx-auto">
                  <i className={`${pilier.icon} text-copper text-xl`} />
                </div>
                <h3 className="font-manrope font-bold text-slate-primary text-base md:text-lg mb-2">
                  {t(pilier.titleKey)}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t(pilier.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}